import { useState } from "react"
import Layout from "@/components/Layout"
import DepreciationScheduleTable, { ScheduleRow } from "@/components/DepreciationScheduleTable"
import { exportToCSV } from "@/lib/exportCsv"
import {Building, ChevronDown, ChevronUp, Info} from 'lucide-react'

// ─── Formatting helpers ───────────────────────────────────────────────────────
function fmt(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
}
function fmtPct(n: number) {
  return n.toFixed(2) + "%"
}

// ─── IRS GDS Half-Year Convention percentage tables (Rev. Proc. 87-57) ───────
// These are the exact IRS published rates. Percentages sum to ~100% (rounding).
const GDS_TABLES: Record<string, number[]> = {
  "3":    [33.33, 44.45, 14.81, 7.41],
  "5":    [20.00, 32.00, 19.20, 11.52, 11.52,  5.76],
  "7":    [14.29, 24.49, 17.49, 12.49,  8.93,  8.92,  8.93,  4.46],
  "10":   [10.00, 18.00, 14.40, 11.52,  9.22,  7.37,  6.55,  6.55,  6.56,  6.55,  3.28],
  "15":   [ 5.00,  9.50,  8.55,  7.70,  6.93,  6.23,  5.90,  5.90,  5.91,  5.90,
             5.91,  5.90,  5.91,  5.90,  5.91,  2.95],
  "20":   [ 3.750, 7.219, 6.677, 6.177, 5.713, 5.285, 4.888, 4.522, 4.462, 4.461,
             4.462, 4.461, 4.462, 4.461, 4.462, 4.461, 4.462, 4.461, 4.462, 4.461, 2.231],
  "27.5": Array.from({ length: 28 }, (_, i) => (i === 0 || i === 27 ? 1.364 : 3.636)),
  "39":   Array.from({ length: 40 }, (_, i) => (i === 0 || i === 39 ? 1.391 : 2.564)),
}

// IRS GDS Mid-Quarter Convention (Q1–Q4) for personal property
// Using 5-year as the representative table per IRS Rev. Proc. 87-57 Table A-4 to A-7
// Keys: "<recovery>-<quarter>"
const GDS_MQ_TABLES: Record<string, number[]> = {
  "5-Q1": [35.00, 26.00, 15.60,  9.36, 8.16, 5.88],
  "5-Q2": [25.00, 30.00, 18.00, 10.80, 8.64, 7.56],
  "5-Q3": [15.00, 34.00, 20.40, 12.24, 9.792, 8.568],
  "5-Q4": [ 5.00, 38.00, 22.80, 13.68, 10.944, 9.576],
  "7-Q1": [25.00, 21.43, 15.31, 10.93,  8.75,  8.74,  8.75,  1.09],
  "7-Q2": [17.85, 23.47, 16.76, 11.97,  8.87,  8.87,  8.87,  3.34],
  "7-Q3": [10.71, 25.51, 18.22, 13.02,  9.30,  8.85,  8.86,  5.53],
  "7-Q4": [ 3.57, 27.55, 19.68, 14.06,  9.86,  8.85,  8.86,  7.57],
}

// ADS Straight-Line tables — full recovery period, half-year convention
// Rate = 100 / ADS life, except first/last year = 50% of annual rate
function buildADSTable(adsLife: number): number[] {
  const annual = 100 / adsLife
  const firstLast = annual / 2
  const rows: number[] = [firstLast]
  for (let i = 1; i < adsLife; i++) rows.push(annual)
  rows.push(firstLast)
  return rows
}

const ADS_TABLES: Record<string, number[]> = {
  "3":    buildADSTable(3),
  "5":    buildADSTable(5),
  "7":    buildADSTable(7),
  "10":   buildADSTable(10),
  "15":   buildADSTable(15),
  "20":   buildADSTable(20),
  "27.5": buildADSTable(27.5),
  "30":   buildADSTable(30),
  "40":   buildADSTable(40),
}

// ─── Property class definitions ───────────────────────────────────────────────
interface PropertyClass {
  value: string
  label: string
  adsLife: string
  method: string
  examples: string
}

const GDS_CLASSES: PropertyClass[] = [
  { value: "3",    label: "3-Year Property",              adsLife: "3",    method: "200% DB → SL", examples: "Tractor units, racehorses, small tools" },
  { value: "5",    label: "5-Year Property",              adsLife: "5",    method: "200% DB → SL", examples: "Computers, autos, light trucks, office equipment" },
  { value: "7",    label: "7-Year Property",              adsLife: "7",    method: "200% DB → SL", examples: "Office furniture, fixtures, most machinery" },
  { value: "10",   label: "10-Year Property",             adsLife: "10",   method: "200% DB → SL", examples: "Water transport, fruit/nut trees, single-purpose agricultural" },
  { value: "15",   label: "15-Year Property",             adsLife: "15",   method: "150% DB → SL", examples: "Land improvements, fences, retail motor fuel outlets" },
  { value: "20",   label: "20-Year Property",             adsLife: "20",   method: "150% DB → SL", examples: "Farm buildings, municipal sewers" },
  { value: "27.5", label: "27.5-Year Residential Rental", adsLife: "27.5", method: "Straight Line",  examples: "Rental houses, apartments, mobile homes" },
  { value: "39",   label: "39-Year Nonresidential Real",  adsLife: "40",   method: "Straight Line",  examples: "Office buildings, retail stores, warehouses" },
]

type Convention = "half-year" | "mid-quarter-q1" | "mid-quarter-q2" | "mid-quarter-q3" | "mid-quarter-q4" | "mid-month"
type DepSystem = "GDS" | "ADS"

// ─── Core calculation engine ──────────────────────────────────────────────────
function calcMACRS(
  cost: number,
  sec179: number,
  bonusRate: number,
  propertyClass: string,
  convention: Convention,
  system: DepSystem,
): ScheduleRow[] {
  // Reduce depreciable basis by Section 179 and bonus depreciation
  const sec179Amt  = Math.min(sec179, cost)
  const afterSec179 = cost - sec179Amt
  const bonusAmt   = (bonusRate / 100) * afterSec179
  const depBasis   = afterSec179 - bonusAmt

  if (depBasis <= 0) {
    // Entire cost expensed in Year 1
    return [{
      year: 1,
      beginningValue: cost,
      depreciation: cost,
      accumulatedDepreciation: cost,
      endingBookValue: 0,
    }]
  }

  let percentages: number[]

  if (system === "ADS") {
    const adsKey = GDS_CLASSES.find((c) => c.value === propertyClass)?.adsLife ?? propertyClass
    percentages = ADS_TABLES[adsKey] ?? buildADSTable(parseFloat(adsKey))
  } else {
    // GDS — choose table based on convention
    if (convention.startsWith("mid-quarter")) {
      const q = convention.split("-")[2].toUpperCase() // Q1..Q4
      const mqKey = `${propertyClass}-${q}`
      percentages = GDS_MQ_TABLES[mqKey] ?? GDS_TABLES[propertyClass] ?? []
    } else {
      percentages = GDS_TABLES[propertyClass] ?? []
    }
  }

  if (percentages.length === 0) return []

  let accumulated = bonusAmt + sec179Amt   // bonus + Sec179 already "used"
  const rows: ScheduleRow[] = []

  // Year 1: include bonus + Sec179 + regular MACRS in first row
  const yr1Regular = (percentages[0] / 100) * depBasis
  const yr1Total   = sec179Amt + bonusAmt + yr1Regular
  accumulated = sec179Amt + bonusAmt + yr1Regular

  rows.push({
    year: 1,
    beginningValue: cost,
    depreciation: yr1Total,
    accumulatedDepreciation: accumulated,
    endingBookValue: Math.max(0, cost - accumulated),
  })

  // Remaining years
  for (let i = 1; i < percentages.length; i++) {
    const begin = rows[rows.length - 1].endingBookValue
    const depr  = (percentages[i] / 100) * depBasis
    accumulated += depr
    rows.push({
      year: i + 1,
      beginningValue: begin,
      depreciation: depr,
      accumulatedDepreciation: accumulated,
      endingBookValue: Math.max(0, cost - accumulated),
    })
  }

  return rows
}

// ─── FAQ data ─────────────────────────────────────────────────────────────────
const FAQS = [
  {
    q: "What is the MACRS depreciation formula?",
    a: "MACRS Depreciation = Asset Cost × IRS Percentage Rate (from Rev. Proc. 87-57 tables). The percentage varies by property class and the year of the recovery period. No salvage value is assumed — MACRS depreciates the full cost basis to zero.",
  },
  {
    q: "What is the difference between GDS and ADS?",
    a: "GDS (General Depreciation System) uses accelerated methods (200% or 150% declining balance switching to straight line) and shorter recovery periods. ADS (Alternative Depreciation System) uses straight-line depreciation over longer ADS lives and is required for certain property (e.g., listed property used ≤50% for business, property used outside the U.S., or when elected by the taxpayer).",
  },
  {
    q: "What is the half-year convention in MACRS?",
    a: "The half-year convention assumes all assets are placed in service at the midpoint of the tax year, regardless of the actual date. This means you claim half a year of depreciation in both the first and last years of the recovery period. It applies to most personal property under GDS.",
  },
  {
    q: "When must I use the mid-quarter convention?",
    a: "If more than 40% of the total depreciable personal property placed in service during the year is placed in service in the last quarter (Q4), you must use the mid-quarter convention for ALL personal property placed in service that year.",
  },
  {
    q: "Can I combine Section 179 with MACRS?",
    a: "Yes. Section 179 allows you to immediately expense up to $1,160,000 (2023 limit) of qualifying property. You apply Section 179 first, then bonus depreciation on the remainder, then MACRS on the reduced basis. This calculator applies them in the correct IRS-prescribed order.",
  },
  {
    q: "What is bonus depreciation under MACRS?",
    a: "Bonus depreciation (also called special depreciation allowance) lets you deduct an additional percentage of the adjusted basis in the first year. Under the Tax Cuts and Jobs Act (TCJA), the rate was 100% through 2022 and phases down 20% per year: 80% in 2023, 60% in 2024, 40% in 2025, and 20% in 2026.",
  },
  {
    q: "How do I report MACRS depreciation on my tax return?",
    a: "All MACRS deductions — including Section 179 and bonus depreciation — are reported on IRS Form 4562 (Depreciation and Amortization). This form is attached to your business tax return (Schedule C, Form 1120, Form 1065, etc.).",
  },
]

// ─── Component ────────────────────────────────────────────────────────────────
export default function MACRSCalculator() {
  // Inputs
  const [cost, setCost]               = useState("")
  const [sec179, setSec179]           = useState("0")
  const [bonusRate, setBonusRate]     = useState("0")
  const [propertyClass, setPropertyClass] = useState("7")
  const [convention, setConvention]   = useState<Convention>("half-year")
  const [system, setSystem]           = useState<DepSystem>("GDS")
  const [yearPlaced, setYearPlaced]   = useState(new Date().getFullYear().toString())

  // Output
  const [schedule, setSchedule]       = useState<ScheduleRow[]>([])
  const [summary, setSummary]         = useState<{
    yr1: number; totalDepr: number; bonusAmt: number; sec179Amt: number; depBasis: number
  } | null>(null)
  const [error, setError]             = useState("")
  const [showRateTable, setShowRateTable] = useState(false)
  const [openFaq, setOpenFaq]         = useState<number | null>(null)

  // ── Validation & calculation ──
  function calculate() {
    setError("")
    const c  = parseFloat(cost)
    const s  = parseFloat(sec179) || 0
    const b  = parseFloat(bonusRate) || 0
    const yr = parseInt(yearPlaced) || new Date().getFullYear()

    if (isNaN(c) || c <= 0)          { setError("Please enter a valid asset cost greater than $0."); return }
    if (s < 0 || s > c)              { setError("Section 179 deduction cannot exceed the asset cost."); return }
    if (b < 0 || b > 100)            { setError("Bonus depreciation rate must be between 0% and 100%."); return }
    if (yr < 1986 || yr > 2100)      { setError("Please enter a valid year placed in service (1986–2100)."); return }

    const rows = calcMACRS(c, s, b, propertyClass, convention, system)
    if (rows.length === 0) { setError("Could not compute schedule. Please check your inputs."); return }

    const sec179Amt = Math.min(s, c)
    const afterSec  = c - sec179Amt
    const bonusAmt  = (b / 100) * afterSec
    const depBasis  = afterSec - bonusAmt
    const total     = rows.reduce((sum, r) => sum + r.depreciation, 0)

    setSchedule(rows)
    setSummary({ yr1: rows[0]?.depreciation ?? 0, totalDepr: total, bonusAmt, sec179Amt, depBasis })
  }

  function handleExport() {
    exportToCSV(schedule, "macrs-depreciation-schedule.csv")
  }

  const selectedClass = GDS_CLASSES.find((c) => c.value === propertyClass)
  const activeRates   = system === "ADS"
    ? (ADS_TABLES[selectedClass?.adsLife ?? propertyClass] ?? buildADSTable(parseFloat(selectedClass?.adsLife ?? propertyClass)))
    : (GDS_TABLES[propertyClass] ?? [])

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* ── Page header ── */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-blue-700 rounded-lg p-2">
              <Building className="w-6 h-6 text-white" />
            </div>
            <span className="text-sm font-medium text-blue-700 uppercase tracking-wide">
              IRS MACRS Tax Depreciation
            </span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            MACRS Depreciation Calculator — GDS &amp; ADS
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl">
            Compute IRS MACRS depreciation using official GDS or ADS tables, half-year or
            mid-quarter conventions, Section 179 expensing, and bonus depreciation — all in
            one free tool for Form 4562 preparation.
          </p>
        </div>

        {/* ── Calculator card ── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 mb-10">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Asset &amp; Tax Details</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">

            {/* Asset Cost */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Asset Cost ($) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="e.g. 25,000"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Year Placed in Service */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year Placed in Service
              </label>
              <input
                type="number"
                min="1986"
                max="2100"
                value={yearPlaced}
                onChange={(e) => setYearPlaced(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Depreciation System */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Depreciation System
              </label>
              <select
                value={system}
                onChange={(e) => setSystem(e.target.value as DepSystem)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
              >
                <option value="GDS">GDS — General Depreciation System</option>
                <option value="ADS">ADS — Alternative Depreciation System</option>
              </select>
            </div>

            {/* Property Class */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                MACRS Property Class
              </label>
              <select
                value={propertyClass}
                onChange={(e) => setPropertyClass(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
              >
                {GDS_CLASSES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              {selectedClass && (
                <p className="text-xs text-gray-500 mt-1">
                  Examples: {selectedClass.examples}
                </p>
              )}
            </div>

            {/* Convention */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Convention
              </label>
              <select
                value={convention}
                onChange={(e) => setConvention(e.target.value as Convention)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
              >
                <option value="half-year">Half-Year (default — most property)</option>
                <option value="mid-quarter-q1">Mid-Quarter — Q1 (Jan–Mar)</option>
                <option value="mid-quarter-q2">Mid-Quarter — Q2 (Apr–Jun)</option>
                <option value="mid-quarter-q3">Mid-Quarter — Q3 (Jul–Sep)</option>
                <option value="mid-quarter-q4">Mid-Quarter — Q4 (Oct–Dec)</option>
                <option value="mid-month">Mid-Month (real property only)</option>
              </select>
            </div>

            {/* Section 179 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Section 179 Deduction ($)
              </label>
              <input
                type="number"
                min="0"
                value={sec179}
                onChange={(e) => setSec179(e.target.value)}
                placeholder="0"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Max $1,160,000 (2023). Enter 0 to skip.</p>
            </div>

            {/* Bonus Depreciation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bonus Depreciation Rate (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={bonusRate}
                onChange={(e) => setBonusRate(e.target.value)}
                placeholder="e.g. 60"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">2024: 60% · 2025: 40% · 2026: 20%</p>
            </div>

          </div>

          {/* Error */}
          {error ? (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5 text-red-700 text-sm">
              <Info className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          ) : null}

          <button
            onClick={calculate}
            className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-8 py-3 rounded-lg transition-colors shadow-sm"
          >
            Calculate MACRS Depreciation
          </button>

          {/* ── Results summary cards ── */}
          {summary !== null ? (
            <div className="mt-8">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
                <div className="bg-blue-700 text-white rounded-xl p-4 text-center col-span-2 sm:col-span-1">
                  <div className="text-2xl font-bold">{fmt(summary.yr1)}</div>
                  <div className="text-blue-100 text-xs mt-1 leading-tight">Year 1 Total Deduction</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <div className="text-xl font-bold text-blue-700">{propertyClass} yrs</div>
                  <div className="text-gray-500 text-xs mt-1">Recovery Period</div>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <div className="text-xl font-bold text-green-700">{fmt(summary.sec179Amt)}</div>
                  <div className="text-gray-500 text-xs mt-1">Section 179</div>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <div className="text-xl font-bold text-amber-700">{fmt(summary.bonusAmt)}</div>
                  <div className="text-gray-500 text-xs mt-1">Bonus Depreciation</div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <div className="text-xl font-bold text-gray-700">{fmt(summary.totalDepr)}</div>
                  <div className="text-gray-500 text-xs mt-1">Total Deductions</div>
                </div>
              </div>

              {/* Basis breakdown note */}
              {(summary.sec179Amt > 0 || summary.bonusAmt > 0) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800 mb-4">
                  <strong>Basis Note:</strong> Asset cost {fmt(parseFloat(cost))} − Section 179{" "}
                  {fmt(summary.sec179Amt)} − Bonus depreciation {fmt(summary.bonusAmt)} ={" "}
                  <strong>MACRS depreciable basis {fmt(summary.depBasis)}</strong>
                </div>
              )}

              {/* Schedule table */}
              <DepreciationScheduleTable rows={schedule} onExport={handleExport} />
            </div>
          ) : null}
        </div>

        {/* ── IRS Rate Table toggle ── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm mb-10">
          <button
            onClick={() => setShowRateTable((v) => !v)}
            className="w-full flex items-center justify-between px-8 py-5 text-left"
          >
            <span className="text-lg font-semibold text-gray-900">
              IRS MACRS Rate Table — {system} {propertyClass}-Year ({convention.replace(/-/g, " ")})
            </span>
            {showRateTable ? (
              <ChevronUp className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            )}
          </button>
          {showRateTable && (
            <div className="px-8 pb-8">
              <p className="text-sm text-gray-600 mb-4">
                Official IRS depreciation percentages from Rev. Proc. 87-57 for{" "}
                <strong>{system} {propertyClass}-year property</strong> with{" "}
                <strong>{convention.replace(/-/g, " ")} convention</strong>.
                Each percentage is applied to the <em>original cost basis</em> (not book value).
              </p>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-800 text-white">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">Year</th>
                      <th className="px-4 py-3 text-right font-semibold">IRS Rate (%)</th>
                      <th className="px-4 py-3 text-right font-semibold">Deduction on $10,000 Asset</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeRates.map((pct, i) => (
                      <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="px-4 py-2 font-medium text-blue-700">Year {i + 1}</td>
                        <td className="px-4 py-2 text-right text-gray-800 font-mono">{fmtPct(pct)}</td>
                        <td className="px-4 py-2 text-right text-green-700 font-mono">
                          {fmt((pct / 100) * 10000)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-100 font-semibold">
                    <tr>
                      <td className="px-4 py-3 text-gray-900">Total</td>
                      <td className="px-4 py-3 text-right text-gray-700 font-mono">
                        {fmtPct(activeRates.reduce((s, r) => s + r, 0))}
                      </td>
                      <td className="px-4 py-3 text-right text-green-700 font-mono">
                        {fmt((activeRates.reduce((s, r) => s + r, 0) / 100) * 10000)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* ══════════════════════ SEO CONTENT SECTION ══════════════════════ */}
        <div className="space-y-12">

          {/* ── What is MACRS ── */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              What Is MACRS? — Modified Accelerated Cost Recovery System
            </h2>
            <p className="text-gray-700 mb-4">
              <strong>MACRS (Modified Accelerated Cost Recovery System)</strong> is the U.S. federal
              tax depreciation system mandated by the IRS for most business property placed in service
              after December 31, 1986. Established under the Tax Reform Act of 1986, MACRS replaced
              the Accelerated Cost Recovery System (ACRS) and allows businesses to recover the adjusted
              basis of qualifying property over a set <em>recovery period</em> using IRS-published
              percentage tables — no salvage value required.
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-700 rounded-r-xl p-6 mb-6">
              <p className="text-base font-semibold text-blue-900 mb-1">MACRS Depreciation Formula</p>
              <p className="font-mono text-blue-800 text-sm leading-relaxed">
                Annual MACRS Deduction = Asset Cost Basis × IRS Applicable Percentage
                <br />
                Depreciable Basis = Cost − Section 179 − Bonus Depreciation
                <br />
                Accumulated Depreciation (Year n) = Sum of all prior deductions
              </p>
            </div>
            <p className="text-gray-700">
              Unlike the <strong>straight-line depreciation formula</strong> — which spreads the cost
              evenly over the useful life — MACRS uses <em>accelerated</em> front-loaded rates that
              deliver larger deductions in the early years of an asset's life, improving cash flow and
              reducing the present-value cost of taxation.
            </p>
          </section>

          {/* ── GDS vs ADS ── */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              GDS vs. ADS: Choosing Your MACRS System
            </h2>
            <p className="text-gray-700 mb-5">
              MACRS contains two sub-systems. The <strong>General Depreciation System (GDS)</strong> is
              used by default for most business property; the <strong>Alternative Depreciation System
              (ADS)</strong> is required in certain situations and uses straight-line depreciation over
              a longer ADS life.
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-200 mb-5">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Factor</th>
                    <th className="px-4 py-3 text-left font-semibold text-blue-700">GDS</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">ADS</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Depreciation method", "200%/150% DB switching to SL", "Straight-line only"],
                    ["Recovery period", "Shorter (3–39 yrs)", "Longer ADS life (3–50 yrs)"],
                    ["Year 1 deduction", "Larger (accelerated)", "Smaller (even spread)"],
                    ["When required", "Default for most property", "Listed property ≤50% business use; property used outside U.S.; AMT calculation; elected by taxpayer"],
                    ["Eligible for bonus depreciation", "Yes (personal property)", "No"],
                    ["IRS publication", "Rev. Proc. 87-57, Table A", "Rev. Proc. 87-57, Table B"],
                  ].map(([f, gds, ads], i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-4 py-3 font-medium text-gray-700">{f}</td>
                      <td className="px-4 py-3 text-blue-700">{gds}</td>
                      <td className="px-4 py-3 text-gray-600">{ads}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Property Classes ── */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              MACRS Property Classes &amp; Rates of Depreciation
            </h2>
            <p className="text-gray-700 mb-5">
              Every depreciable asset is assigned to a <strong>MACRS property class</strong> based on
              its <em>class life</em> (the IRS-assigned useful life from Rev. Proc. 87-56). The property
              class determines the <strong>recovery period</strong>, the depreciation method, and the
              applicable percentage tables. The following table summarizes all GDS classes:
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-200 mb-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">GDS Class</th>
                    <th className="px-4 py-3 text-left font-semibold">ADS Life</th>
                    <th className="px-4 py-3 text-left font-semibold">Method</th>
                    <th className="px-4 py-3 text-left font-semibold">Year 1 Rate</th>
                    <th className="px-4 py-3 text-left font-semibold">Example Assets</th>
                  </tr>
                </thead>
                <tbody>
                  {GDS_CLASSES.map((cls, i) => (
                    <tr key={cls.value} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                      <td className="px-4 py-3 font-semibold text-blue-700">{cls.label}</td>
                      <td className="px-4 py-3 text-gray-600">{cls.adsLife} yrs</td>
                      <td className="px-4 py-3 text-gray-600">{cls.method}</td>
                      <td className="px-4 py-3 font-mono text-green-700">
                        {fmtPct(GDS_TABLES[cls.value]?.[0] ?? 0)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{cls.examples}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500">
              Source: IRS Rev. Proc. 87-57, IRS Publication 946 (How to Depreciate Property).
              Year 1 rates shown are for GDS half-year convention.
            </p>
          </section>

          {/* ── Conventions explained ── */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              MACRS Conventions: Half-Year, Mid-Quarter, and Mid-Month
            </h2>
            <p className="text-gray-700 mb-5">
              MACRS <strong>conventions</strong> determine how much depreciation is allowed in the first
              and last years of the recovery period. They exist because assets are purchased throughout
              the year, not just on January 1st.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {[
                {
                  title: "Half-Year Convention",
                  color: "blue",
                  when: "Default for all personal property (3–20 year classes).",
                  rule: "All assets are treated as placed in service on July 1. You get half a year of depreciation in Year 1 and half a year in the final recovery year.",
                  tip: "Most common — use this unless mid-quarter applies."
                },
                {
                  title: "Mid-Quarter Convention",
                  color: "amber",
                  when: "Required when >40% of personal property is placed in service in Q4.",
                  rule: "Assets are treated as placed in service at the midpoint of the quarter in which they were actually placed in service.",
                  tip: "Applies to ALL personal property that year, not just Q4 assets."
                },
                {
                  title: "Mid-Month Convention",
                  color: "green",
                  when: "Required for all residential and nonresidential real property (27.5-yr and 39-yr).",
                  rule: "The asset is treated as placed in service on the 15th of the month in which it was placed in service.",
                  tip: "Always used with real property — never with personal property."
                },
              ].map((conv) => (
                <div key={conv.title} className={`rounded-xl border p-5 ${
                  conv.color === "blue"  ? "border-blue-200 bg-blue-50" :
                  conv.color === "amber" ? "border-amber-200 bg-amber-50" :
                  "border-green-200 bg-green-50"
                }`}>
                  <h3 className={`font-semibold mb-2 ${
                    conv.color === "blue"  ? "text-blue-900" :
                    conv.color === "amber" ? "text-amber-900" :
                    "text-green-900"
                  }`}>{conv.title}</h3>
                  <p className="text-xs text-gray-700 mb-1"><strong>When:</strong> {conv.when}</p>
                  <p className="text-xs text-gray-700 mb-1"><strong>Rule:</strong> {conv.rule}</p>
                  <p className={`text-xs font-medium mt-2 ${
                    conv.color === "blue"  ? "text-blue-700" :
                    conv.color === "amber" ? "text-amber-700" :
                    "text-green-700"
                  }`}>💡 {conv.tip}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── Section 179 & Bonus ── */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Section 179 &amp; Bonus Depreciation — First-Year Expensing
            </h2>
            <p className="text-gray-700 mb-4">
              Before computing MACRS depreciation, the IRS requires you to apply first-year
              expensing options in a specific order to reduce the depreciable basis:
            </p>
            <ol className="list-decimal list-inside space-y-3 text-gray-700 mb-6 ml-2">
              <li>
                <strong>Step 1 — Section 179:</strong> Elect to immediately expense up to
                $1,160,000 (2023) of qualifying personal property. Subject to a taxable income
                limitation and a phase-out threshold ($2,890,000 in 2023).
              </li>
              <li>
                <strong>Step 2 — Bonus Depreciation (Special Allowance):</strong> Apply the
                applicable bonus rate to the remaining basis after Section 179. The TCJA rate
                was 100% through 2022 and phases down: 80% (2023), 60% (2024), 40% (2025), 20%
                (2026), 0% (2027+).
              </li>
              <li>
                <strong>Step 3 — Regular MACRS:</strong> Apply the applicable GDS or ADS percentage
                tables to the reduced depreciable basis.
              </li>
            </ol>
            <div className="bg-gray-900 rounded-xl p-5 font-mono text-sm text-green-400 leading-loose">
              <span className="text-gray-400">// Example: $50,000 computer (5-Year GDS)</span><br />
              <span className="text-white">Cost Basis</span>{"          = $50,000"}<br />
              <span className="text-white">− Section 179</span>{"     = $20,000 (elected)"}<br />
              <span className="text-white">After Sec. 179</span>{"    = $30,000"}<br />
              <span className="text-white">− Bonus (60%)</span>{"     = $18,000"}<br />
              <span className="text-white">MACRS Basis</span>{"       = $12,000"}<br />
              <span className="text-yellow-400">Year 1 MACRS</span>{"      = $12,000 × 20.00% = $2,400"}<br />
              <span className="text-green-400">{"Total Yr 1 Deduction = $20,000 + $18,000 + $2,400 = $40,400"}</span>
            </div>
          </section>

          {/* ── How to Calculate Accumulated Depreciation ── */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              How to Calculate Accumulated Depreciation Under MACRS
            </h2>
            <p className="text-gray-700 mb-4">
              <strong>Accumulated depreciation</strong> is the running total of all depreciation
              deductions taken on an asset since it was placed in service. Under MACRS, you track it
              year by year using the formula:
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-700 rounded-r-xl p-5 mb-5">
              <p className="font-mono text-blue-900 text-sm leading-loose">
                Accumulated Depreciation (Year n) = Σ (Cost × MACRS Rate) for Years 1 through n<br />
                Ending Book Value (Year n) = Original Cost − Accumulated Depreciation (Year n)
              </p>
            </div>
            <p className="text-gray-700 mb-4">
              For example, a <strong>$25,000 asset in the 7-year class</strong> (GDS, half-year):
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Year</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">IRS Rate</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Deduction</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Accum. Depr.</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700">Book Value</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const base = 25000
                    const rates = GDS_TABLES["7"] ?? []
                    let accum = 0
                    return rates.map((r, i) => {
                      const depr = (r / 100) * base
                      accum += depr
                      return (
                        <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                          <td className="px-3 py-2 text-blue-700 font-medium">Year {i + 1}</td>
                          <td className="px-3 py-2 text-right font-mono text-gray-700">{fmtPct(r)}</td>
                          <td className="px-3 py-2 text-right font-mono text-green-700">{fmt(depr)}</td>
                          <td className="px-3 py-2 text-right font-mono text-gray-600">{fmt(accum)}</td>
                          <td className="px-3 py-2 text-right font-mono text-gray-900">{fmt(Math.max(0, base - accum))}</td>
                        </tr>
                      )
                    })
                  })()}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Form 4562 ── */}
          <section>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-7">
              <h2 className="text-xl font-bold text-amber-900 mb-3">
                Reporting MACRS on IRS Form 4562
              </h2>
              <p className="text-amber-800 text-sm mb-4">
                All depreciation, Section 179, and bonus depreciation deductions must be reported
                annually on <strong>IRS Form 4562 — Depreciation and Amortization</strong>. Key
                sections of Form 4562:
              </p>
              <ul className="text-amber-800 text-sm space-y-2 list-disc list-inside">
                <li><strong>Part I (Lines 1–12):</strong> Section 179 election — list each asset and elected amount.</li>
                <li><strong>Part II (Lines 14–15):</strong> Special depreciation allowance (bonus depreciation).</li>
                <li><strong>Part III (Lines 19–26):</strong> MACRS depreciation for current-year and prior-year assets.</li>
                <li><strong>Part VI (Lines 42–44):</strong> Amortization of intangibles (Section 197 assets).</li>
              </ul>
              <p className="text-amber-700 text-xs mt-4">
                ⚠️ Depreciation Hub provides educational tools only. Always consult a licensed CPA or
                tax attorney for filing advice. IRS limits and phase-out thresholds change annually.
              </p>
            </div>
          </section>

          {/* ── FAQ ── */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              MACRS Depreciation — Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {FAQS.map((faq, i) => (
                <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900 pr-4">{faq.q}</span>
                    {openFaq === i
                      ? <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      : <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    }
                  </button>
                  {openFaq === i && (
                    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 text-sm text-gray-700 leading-relaxed">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

        </div>
      </div>
    </Layout>
  )
}
